import { agent } from '@shared/services/agent';
import moment, { Moment } from 'moment';
import { getStoreRef } from '@shared/services/storeRef';
import { warningNotification } from '@shared/store/notification.duck';
import {
  getHidePastWhereaboutsCutoff,
  hiddenPastWhereaboutsMessage,
} from '@shared/hooks/UseHidePastWhereaboutsCutoff';
import {
  MemberMovements,
  SimpleUserWithLocation,
  UsersWhereaboutsDay, WhereaboutsDayV2
} from '@features/whereabouts/models/movements.models';
import {
  CompanyMovementResponse,
  DailyWhereaboutsResponse,
  TeamMovementResponse,
  UpdateWhereaboutsResponse,
  WhereaboutsTeamViewResponse
} from '@shared/models/api.models';
import { Tag } from "@shared/models/tags.models";
import { DATE_FORMAT } from "@shared/utils/DateUtils";

// Mirrors the backend WhereaboutsViewAccessService check so requests the
// server would reject with a 403 are never sent; explains why instead.
// A blocked view fires several requests in parallel, so the notification is
// throttled to avoid stacking identical toasts.
let lastBlockedNotificationAt = 0;
function verifyWhereaboutsRequestable(date: Moment | string) {
  const store = getStoreRef();
  if (!store) return;
  const requestedDate = moment.isMoment(date) ? date : moment(date);
  if (!requestedDate.isValid()) return;
  const { minDate, weeksLimit } = getHidePastWhereaboutsCutoff(store.getState());
  if (minDate && requestedDate.isBefore(minDate)) {
    const message = hiddenPastWhereaboutsMessage(weeksLimit);
    if (Date.now() - lastBlockedNotificationAt > 3000) {
      lastBlockedNotificationAt = Date.now();
      store.dispatch(warningNotification(message));
    }
    throw new Error(message);
  }
}

export const makeWhereaboutsScheduleRequest = async (usersWhereabouts: UsersWhereaboutsDay[], startDate: String, numberOfWeeks: number, userId: string) => {
  return agent.requests.post('/api/whereabouts/schedule', {
    whereabouts: usersWhereabouts, startDate, numberOfWeeks, userId
  });
}

const getCompanyWeeklyMovements = async (companyId: string | null, calendarDate: Moment): Promise<CompanyMovementResponse> => {
  if (companyId === null) {
    throw new Error();
  }
  verifyWhereaboutsRequestable(calendarDate);
  const response: CompanyMovementResponse = (await agent.requests.get(`/api/whereabouts/company?cid=${companyId}&week=${calendarDate.format(DATE_FORMAT)}`));
  return {
    ...response,
  }
}

export async function requestWhereaboutsV2(calendarDate: Moment): Promise<WhereaboutsTeamViewResponse> {
  verifyWhereaboutsRequestable(calendarDate);
  let qs = `?week=${calendarDate.format(DATE_FORMAT)}`;
  return await (agent.requests.get(`/api/v3/whereabouts` + qs));
}

export async function requestLineReportsWhereabouts(calendarDateString: string): Promise<MemberMovements[]> {
  verifyWhereaboutsRequestable(calendarDateString);
  let qs = `?week=${calendarDateString}`;
  return await (agent.requests.get(`/api/v2/whereabouts/line-reports` + qs));
}

export async function requestFavouritedUsersWhereabouts(calendarDateString: string): Promise<MemberMovements[]> {
  verifyWhereaboutsRequestable(calendarDateString);
  let qs = `?week=${calendarDateString}`;
  return await (agent.requests.get(`/api/v2/whereabouts/favourited` + qs));
}

export const requestTeamWeeklyMovements = async (teamId: string | null, calendarDate: Moment): Promise<TeamMovementResponse> => {
  if (teamId === null) {
    throw new Error();
  }
  verifyWhereaboutsRequestable(calendarDate);
  const response: TeamMovementResponse = (await agent.requests.get(`/api/whereabouts/team?teamId=${teamId}&week=${calendarDate.format(DATE_FORMAT)}`, true));
  return {
    ...response,
  }
}

export const requestUsersWeeklyMovements = async (calendarDate: Moment, userId: string): Promise<MemberMovements> => {
  const weekCommencing = calendarDate.clone().isoWeekday(1);
  verifyWhereaboutsRequestable(weekCommencing);
  return (await agent.requests.get(`/api/whereabouts/user?week=${weekCommencing.format(DATE_FORMAT)}&userId=${userId}`));
}

export const getUsersInOfficeOn = async (calendarDate: Moment, officeId: number): Promise<SimpleUserWithLocation[]> => {
  verifyWhereaboutsRequestable(calendarDate);
  return (await agent.requests.get(`/api/whereabouts/office?date=${calendarDate.format(DATE_FORMAT)}&office=${officeId}`));
}

export function getMovementsByTags(calendarDate: Moment, tags: Tag[]): Promise<MemberMovements[]> {
  verifyWhereaboutsRequestable(calendarDate);
  return agent.requests.get(`/api/whereabouts/tag?week=${calendarDate.format(DATE_FORMAT)}&ids=${tags.map(at => at.id).join(',')}`);
}

export function acceptAndSyncMovements(date: Moment): Promise<void> {
  return agent.requests.post(`/api/whereabouts/resync?date=${date.format(DATE_FORMAT)}`);
}

export function acceptAndSyncAllMovements(): Promise<void> {
  return agent.requests.post(`/api/whereabouts/resync/all`);
}

export function sendWhereaboutsReminder(userId: string, date: Moment): Promise<void> {
  return agent.requests.post('/api/whereabouts/reminder', {userIds: [userId], weekCommencingDate: date.format(DATE_FORMAT)})
}

export async function requestDailyWhereabouts(date: string, teamIds: string[], workGroupIds: number[]): Promise<DailyWhereaboutsResponse> {
  verifyWhereaboutsRequestable(date);
  return await agent.requests.post('/api/v3/whereabouts/daily', { date, teamIds, workGroupIds });
}

export function updateWhereaboutsDay(whereaboutsDay: WhereaboutsDayV2): Promise<UpdateWhereaboutsResponse> {
  return agent.requests.put('/api/v3/whereabouts', whereaboutsDay)
}

const Movements = {
  getCompanyWeeklyMovements,
}
